import { ValueGetterParams } from 'ag-grid-community';
import { AgGridReact } from 'ag-grid-react';
import { FC, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Header from '../../common/Header/Header';
import {
  Buttons,
  Page,
  TableWrapper,
} from '../../common/TableWithButtonsRightLayout/TableWithButtonsRightLayout.styles';
import Button from '../../components/Button/Button';
import { Variant } from '../../components/Button/Button.types';
import Table from '../../components/Table/Table';
import { useClasses } from '../../services/class/class.service';
import { Class } from '../../services/class/class.types';
import { useFetchErrorToast } from '../../utils/useFetchErrorToasts/useFetchErrorToast';
import { CSVLink } from 'react-csv';
import Divider from '@mui/material/Divider';
import { createTheme, ThemeProvider } from '@mui/material/styles';

const Classes: FC = () => {
  const navigate = useNavigate();
  const gridRef = useRef<AgGridReact>(null);
  const [selectedRow, setSelectedRow] = useState<Class | undefined>(undefined);
  const { data, isError: classesError } = useClasses(true);
  const muiTheme = createTheme();

  const allClasses = data?.data || [];

  const headers = [
    { label: 'MOA Class No.', key: 'classNo' },
    { label: 'Description', key: 'description' },
    { label: 'Status No.', key: 'statusNo' },
    { label: 'PCA Class No.', key: 'pcaClassNo' },
    { label: 'Price Option No.', key: 'priceOptionNo' },
    { label: 'Print Kitchen', key: 'printInKitchen' },
    { label: 'Allow Discount', key: 'allowDiscount' },
    { label: 'Tax Exempt', key: 'taxExempted' },
    { label: 'Allow Half', key: 'allowHalf' },
    { label: 'Mnemonic', key: 'mnemonic' },
    { label: 'Seq No.', key: 'seqNo' },
    { label: 'Long Description', key: 'longDescription' },
    { label: 'Valid on Web', key: 'validOnWeb' },
    { label: 'Image ID', key: 'imageId' },
    { label: 'BOH Description', key: 'displayDesc' },
    { label: 'Created By', key: 'createdBy' },
    { label: 'Created Date', key: 'createdDate' },
    { label: 'Updated By', key: 'updatedBy' },
    { label: 'Updated Date', key: 'updatedDate' }
  ];

  const csvData = allClasses.map((classData: Class) => ({
    classNo: classData.classNo,
    description: classData.description,
    statusNo: classData.statusNo,
    pcaClassNo: classData.pcaClassNo,
    priceOptionNo: classData.priceOptionNo,
    printInKitchen: classData.printInKitchen ? 'Y' : 'N',
    allowDiscount: classData.allowDiscount ? 'Y' : 'N',
    taxExempted: classData.taxExempted ? 'Y' : 'N',
    allowHalf: classData.allowHalf ? 'Y' : 'N',
    mnemonic: classData.mnemonic,
    seqNo: classData.seqNo,
    longDescription: classData.longDescription,
    validOnWeb: classData.validOnWeb ? 'Y' : 'N',
    displayDesc: classData.displayDesc,
    createdBy: classData.createdBy,
    createdDate: classData.createdDate,
    updatedBy: classData.updatedBy,
    updatedDate: classData.updatedDate
  }));

  const handleRowSelection = () => {
    /* Set the selected row details for later purpose to view */
    const selectedRows = gridRef?.current?.api.getSelectedRows();
    setSelectedRow(selectedRows?.[0]);
  };

  const handleOpenClick = () => {
    navigate(`/tables/classes/${selectedRow?.classNo}`);
  };

  const handleAddNew = () => {
    navigate(`/tables/classes/add`);
  };

  useFetchErrorToast([{ isError: classesError, label: 'Error fetching classes', id: 'classesFetchError' }]);

  return (
    <>
      <Header breadcrumb={{ to: '/tables', label: 'All tables' }} title='Class' />
      <Page>
        <TableWrapper>
          <Table
            rowData={data?.data}
            ref={gridRef}
            returnedRecords={data?.data?.length}
            height={'100%'}
            rowSelection='single'
            onRowSelected={handleRowSelection}
            columnDefs={[
              {
                minWidth: 48,
                field: '',
                headerName: '',
                checkboxSelection: true,
                floatingFilter: false,
                sortable: false,
              },
              { minWidth: 80, field: 'classNo', headerName: 'MOA Class No.' },
              { minWidth: 80, field: 'classNo', headerName: 'Class No.' },
              { minWidth: 112, field: 'description', headerName: 'Description' },
              { minWidth: 80, field: 'statusNo', headerName: 'Status No.' },
              { minWidth: 80, field: 'pcaClassNo', headerName: 'PCA Class No.' },
              { minWidth: 80, field: 'priceOptionNo', headerName: 'Price Option No.' },
              {
                minWidth: 80,
                field: 'printInKitchen',
                headerName: 'Print Kitchen',
                valueFormatter: (params) => (!params.value ? 'N' : 'Y'),
                filterValueGetter: (params: ValueGetterParams) => (!params.getValue('printInKitchen') ? 'N' : 'Y'),
              },
              {
                minWidth: 80,
                field: 'allowDiscount',
                headerName: 'Allow Discount',
                valueFormatter: (params) => (!params.value ? 'N' : 'Y'),
                filterValueGetter: (params: ValueGetterParams) => (!params.getValue('allowDiscount') ? 'N' : 'Y'),
              },
              {
                minWidth: 80,
                field: 'taxExempted',
                headerName: 'Tax Exempt',
                valueFormatter: (params) => (!params.value ? 'N' : 'Y'),
                filterValueGetter: (params: ValueGetterParams) => (!params.getValue('taxExempted') ? 'N' : 'Y'),
              },
              {
                minWidth: 80,
                field: 'allowHalf',
                headerName: 'Allow Half',
                valueFormatter: (params) => (!params.value ? 'N' : 'Y'),
                filterValueGetter: (params: ValueGetterParams) => (!params.getValue('allowHalf') ? 'N' : 'Y'),
              },
              { minWidth: 80, field: 'mnemonic', headerName: 'Mnemonic' },
              { minWidth: 80, field: 'seqNo', headerName: 'Seq No.' },
              { minWidth: 180, field: 'longDescription', headerName: 'Long Description' },
              {
                minWidth: 80,
                field: 'validOnWeb',
                headerName: 'Valid on Web',
                valueFormatter: (params) => (!params.value ? 'N' : 'Y'),
                filterValueGetter: (params: ValueGetterParams) => (!params.getValue('validOnWeb') ? 'N' : 'Y'),
              },
              { minWidth: 112, field: 'displayDesc', headerName: 'BOH Description' },
              {
                minWidth: 80,
                field: 'imageId',
                headerName: 'GUI Image',
                valueFormatter: (params) => (params.value ? 'Y' : 'N'),
                filterValueGetter: (params: ValueGetterParams) => (params.getValue('imageId') ? 'Yes' : 'No'),
              },
            ]}
          />
        </TableWrapper>
        <Buttons>
          <Button
            label='Open'
            variant={Variant.Primary}
            onClick={handleOpenClick}
            disabled={selectedRow === undefined}
          />
          <Button label='Add New' variant={Variant.Secondary} onClick={handleAddNew} />
          <ThemeProvider theme={muiTheme} key={'time-picker-mui-theme'}>
            <Divider orientation='horizontal' variant={'fullWidth'} flexItem />
          </ThemeProvider>
            <CSVLink
              data={csvData}
              headers={headers}
              filename={`Classes_${new Date().toISOString().split('T')[0]}.csv`}
              style={{ textDecoration: 'none', color: 'inherit' }}
            >
              <Button variant={Variant.Secondary} label='Export to CSV'>
              </Button>
            </CSVLink>
        </Buttons>
      </Page>
    </>
  );
};

export default Classes;
